/**
 * LiveKit data-channel protocol for rt sessions — topic 'rt', JSON payloads.
 * FROZEN CONTRACT — do not change shapes without coordinating across agents.
 *
 * - {t:'tc', samples, wallMs}                      studio broadcasts every 250ms while playing
 * - {t:'rec', action:'arm'|'start'|'stop', take, tcSamples, wallMs}   studio commands
 * - {t:'status', role, name, ready, disk?, net?, rec?}                everyone, 5s heartbeat
 */
export type RtRole = 'studio' | 'director' | 'engineer' | 'actor'

export const RT_TOPIC = 'rt'

export interface TcMsg {
  t: 'tc'
  /** samples since midnight @48k */
  samples: number
  wallMs: number
}

export type RecAction = 'arm' | 'start' | 'stop'

export interface RecMsg {
  t: 'rec'
  action: RecAction
  take: number
  tcSamples: number
  wallMs: number
}

export interface StatusMsg {
  t: 'status'
  role: RtRole
  name: string
  ready: boolean
  disk?: { availMB: number; needMB: number }
  net?: { rttMs: number; lossPct: number }
  rec?: { state: string; bytes: number }
  /**
   * APPEND (F3 v2.4, 2026-06-10): 8-item Client Analyzer summary, measured
   * locally on each end (1s getStats sampler) and broadcast with the 5s
   * heartbeat so the studio can render the all-participants matrix.
   * Optional + additive — old clients ignore it.
   */
  sum?: {
    rttMs?: number | null
    lossUpPct?: number | null
    lossDownPct?: number | null
    jitterMs?: number | null
    bitrateKbps?: number | null
    sampleRate?: number | null
    diskFreeGB?: number | null
    recBytes?: number | null
  }
  /**
   * APPEND (F3 v2.7 #8): device echo — guest input/output device labels
   * (truncated to 48 chars) + AEC state, shown on the studio Connection rows.
   */
  dev?: { inputLabel?: string; outputLabel?: string; aec?: boolean }
  /**
   * APPEND (F3 v2.7 #5): pre-flight network verdict carried into the room —
   * the studio Available Connections row shows this color dot.
   */
  preflight?: 'green' | 'yellow' | 'red'
  /**
   * APPEND (G1 v2.11, 2026-06-10): connection phase. Guests on the /join page
   * are connected with phase='preflight' (nothing published, nothing
   * subscribed — chat + heartbeat only); once in the room the heartbeat says
   * 'pool'. 'patched' is reserved for the v2.10 patch-bay pass. Optional +
   * additive — old clients ignore it.
   */
  phase?: 'preflight' | 'pool' | 'patched'
  /**
   * APPEND (G1 v2.9, 2026-06-10): pre-flight grade report — item key →
   * 'PERFECT'|'GREAT'|'OK'|'POOR' (plus an 'overall' key). Streamed during
   * the preflight phase as checks complete and carried into the first in-room
   * heartbeat so the studio Connection row can show the medical report on
   * hover. Optional + additive.
   */
  grades?: Record<string, string>
}

/**
 * APPEND (UI v2, 2026-06-10): session config broadcast — studio → all.
 * Currently carries the BWF wav file-name prefix typed into the SLP
 * "Prefix for remote wav files" box. Additive only: tc/rec/status shapes
 * above are untouched; old clients ignore unknown t values.
 */
export interface CfgMsg {
  t: 'cfg'
  /** Prefix for remote wav files (replaces project_code in the fileBase). */
  wavPrefix: string
  wallMs: number
  /**
   * APPEND (F3 v2.7 #6): actor uplink OPUS bitrate (kbit/s) chosen on the
   * studio Channel Setup panel. Only the ACTOR applies it to its sender —
   * director/engineer uplinks stay fixed at 64 kbit/s mono. Optional +
   * additive; old clients ignore it.
   */
  audioKbps?: number
}

/**
 * APPEND (UI v2 F2, 2026-06-10): workspace-files change notification —
 * broadcast after a studio upload completes. Receivers re-fetch the list via
 * GET /rt/join/:token/files (the message itself carries no file data, so the
 * link-token auth boundary stays server-side). Additive only.
 */
export interface FilesMsg {
  t: 'files'
  /** files now in the session workspace (informational) */
  count?: number
  wallMs: number
}

/**
 * APPEND (G2 v2.10, 2026-06-10): patch-bay wiring — studio drags a pool guest
 * onto a Connection slot. Broadcast by the STUDIO only:
 *   slot 1..3  → guest is patched (ON AIR: unmute + subscribable)
 *   slot null  → guest returns to the waiting pool (mute + mic unsubscribable)
 * The studio re-broadcasts its whole patch table on connect and whenever a
 * participant (re)joins, so refreshes recover state. Additive only.
 */
export interface PatchMsg {
  t: 'patch'
  /** LiveKit participant identity of the guest being (un)patched */
  identity: string
  /** Connection slot 1..3, or null = back to the pool */
  slot: number | null
  wallMs: number
}

export type RtMsg = TcMsg | RecMsg | StatusMsg | CfgMsg | FilesMsg | PatchMsg

/** Participant metadata attached to the LiveKit token by the API. */
export interface RtParticipantMetadata {
  role: RtRole
  name: string
}

const encoder = new TextEncoder()
const decoder = new TextDecoder()

export function encodeRtMsg(msg: RtMsg): Uint8Array {
  return encoder.encode(JSON.stringify(msg))
}

export function decodeRtMsg(payload: Uint8Array): RtMsg | null {
  try {
    const obj = JSON.parse(decoder.decode(payload)) as RtMsg
    if (
      obj &&
      (obj.t === 'tc' ||
        obj.t === 'rec' ||
        obj.t === 'status' ||
        obj.t === 'cfg' ||
        obj.t === 'files' ||
        obj.t === 'patch')
    )
      return obj
    return null
  } catch {
    return null
  }
}

// ---------------------------------------------------------------------------
// Chat (UI v2, 2026-06-10) — separate data-channel topic 'chat'.
// APPEND-ONLY: the frozen 'rt' contract above is untouched; decodeRtMsg
// rejects chat payloads (t==='chat') and decodeChatMsg rejects rt payloads,
// so the two topics stay isolated even if a payload crosses handlers.

export const CHAT_TOPIC = 'chat'

export interface ChatMsg {
  t: 'chat'
  /** sender LiveKit participant identity */
  from: string
  role: RtRole | 'unknown'
  /** sender display name */
  name: string
  /** plain text — always rendered as text, never HTML */
  text: string
  /** wall-clock ms (sender) */
  ts: number
  /** private-message target identity; absent = everyone */
  to?: string
}

function isChatRole(v: unknown): v is RtRole {
  return v === 'studio' || v === 'director' || v === 'engineer' || v === 'actor'
}

export function encodeChatMsg(msg: ChatMsg): Uint8Array {
  return encoder.encode(JSON.stringify(msg))
}

export function decodeChatMsg(payload: Uint8Array): ChatMsg | null {
  try {
    const obj = JSON.parse(decoder.decode(payload)) as Partial<ChatMsg>
    if (
      obj &&
      obj.t === 'chat' &&
      typeof obj.from === 'string' &&
      typeof obj.text === 'string' &&
      typeof obj.ts === 'number'
    ) {
      return {
        t: 'chat',
        from: obj.from,
        role: isChatRole(obj.role) ? obj.role : 'unknown',
        name: typeof obj.name === 'string' && obj.name ? obj.name : obj.from,
        text: obj.text,
        ts: obj.ts,
        ...(typeof obj.to === 'string' && obj.to ? { to: obj.to } : {}),
      }
    }
    return null
  } catch {
    return null
  }
}

export function parseParticipantMetadata(raw: string | undefined): RtParticipantMetadata | null {
  if (!raw) return null
  try {
    const obj = JSON.parse(raw) as Partial<RtParticipantMetadata>
    if (
      obj &&
      (obj.role === 'studio' || obj.role === 'director' || obj.role === 'engineer' || obj.role === 'actor')
    ) {
      return { role: obj.role, name: typeof obj.name === 'string' ? obj.name : obj.role }
    }
    return null
  } catch {
    return null
  }
}
